import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { mdHeight, xsHeight } from "components/AppBar";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FacilityPicker } from "components/FacilityPicker";
import {
  EpisodicEventItemFragment,
  FacilityPickerFragment,
  WasteEpisodicEventStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { FederalGeneratorStatusIndicator } from "../Facilities/Facility/FederalGeneratorStatusIndicator";
import {
  DELETE_EPISODIC_EVENT_MUTATION,
  FACILITY_EPISODIC_EVENTS_QUERY,
  FINALIZE_EPISODIC_EVENT_MUTATION,
  REOPEN_EPISODIC_EVENT_MUTATION,
} from "./api";
import { deleteConfirmCopy, finalizeConfirmCopy, unshippedEventWaste } from "./episodicDisplay";
import { EpisodicEventCard } from "./EpisodicEventCard";
import { EpisodicEventDialog } from "./EpisodicEventDialog";

const YEAR_ALLOWANCE_CAPTION = "1 planned + 1 unplanned per year — 40 CFR 262 subpart L";

/**
 * Shown when the page is reached without a `?facilityId=` — this page has no
 * tenant-wide mode since the schema only exposes episodic events per
 * facility. Duplicated from `AccumulationAreasList`'s private
 * `FacilityPickerPrompt` rather than shared, since both copies are a handful
 * of lines that differ only in their prompt text.
 */
function FacilityPickerPrompt() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // No facility yet, so the trail can't name one — the full facility trail
  // is owned by FacilityEpisodicEvents once a facility is picked.
  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Episodic events" },
  ]);

  const handleChange = (facility: FacilityPickerFragment | null) => {
    if (!facility) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("facilityId", facility.id);
    navigate({ search: next.toString() }, { replace: true });
  };

  return (
    <Stack spacing={2} sx={{ px: 3, py: 4, maxWidth: 420 }}>
      <Typography variant="body1">Select a facility to view its episodic events.</Typography>
      <FacilityPicker
        label="Facility"
        facilityQueryVariant="waste"
        value={null}
        onChange={handleChange}
        invalid={false}
        isTouched={false}
        isDirty={false}
        isValidating={false}
      />
    </Stack>
  );
}

/** Opens the declare dialog. */
function DeclareEventButton({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outlined" size="small" onClick={onClick}>
      Declare new episodic event
    </Button>
  );
}

/**
 * Declare/edit/finalize/delete dialogs. Each is conditionally rendered so a
 * closed dialog unmounts entirely rather than sitting hidden with stale form
 * state — the create/edit form is a fresh `EpisodicEventDialog` mount every
 * time it opens, matching `WasteContainerDialog`'s inline-create pattern for
 * `AccumulationAreaDialog`. Finalize and delete have no dedicated dialog
 * file: both are plain confirmations via the house `ConfirmDialog`.
 */
function EventDialogs({
  facilityId,
  declareOpen,
  onDeclareClose,
  editingEvent,
  onEditClose,
  finalizingEvent,
  onFinalizeCancel,
  deletingEvent,
  onDeleteCancel,
}: {
  facilityId: string;
  declareOpen: boolean;
  onDeclareClose: () => void;
  editingEvent: EpisodicEventItemFragment | null;
  onEditClose: () => void;
  finalizingEvent: EpisodicEventItemFragment | null;
  onFinalizeCancel: () => void;
  deletingEvent: EpisodicEventItemFragment | null;
  onDeleteCancel: () => void;
}) {
  const alerts = useAlerts();
  const [finalizeEvent, { loading: isFinalizing }] = useMutation(FINALIZE_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });
  const [deleteEvent, { loading: isDeleting }] = useMutation(DELETE_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });

  const handleConfirmFinalize = async () => {
    if (!finalizingEvent) {
      return;
    }
    await finalizeEvent({
      variables: { id: finalizingEvent.id },
      onCompleted: () => {
        alerts.success("Episodic event finalized");
        onFinalizeCancel();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while finalizing the event");
      },
    });
  };

  const handleConfirmDelete = async () => {
    if (!deletingEvent) {
      return;
    }
    await deleteEvent({
      variables: { id: deletingEvent.id },
      onCompleted: () => {
        alerts.success("Episodic event deleted");
        onDeleteCancel();
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while deleting the event");
      },
    });
  };

  return (
    <>
      {declareOpen && (
        <EpisodicEventDialog open mode="declare" facilityId={facilityId} onClose={onDeclareClose} />
      )}
      {editingEvent && (
        <EpisodicEventDialog
          open
          mode="edit"
          facilityId={facilityId}
          event={editingEvent}
          onClose={onEditClose}
        />
      )}
      <ConfirmDialog
        open={finalizingEvent != null}
        onClose={onFinalizeCancel}
        onConfirm={handleConfirmFinalize}
        loading={isFinalizing}
        header="Finalize this event?"
        info={finalizeConfirmationInfo(finalizingEvent)}
        confirmText="Finalize event"
      />
      <ConfirmDialog
        open={deletingEvent != null}
        onClose={onDeleteCancel}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        header="Delete this event?"
        info={deleteConfirmationInfo(deletingEvent)}
        confirmText="Delete event"
      />
    </>
  );
}

/**
 * Finalize-confirmation copy for a specific event, or the empty string while
 * no event is being finalized (the dialog is closed). Delegates to the pure
 * `finalizeConfirmCopy` builder so the wording lives alongside the other
 * episodic copy builders in `episodicDisplay.ts`.
 */
function finalizeConfirmationInfo(event: EpisodicEventItemFragment | null): string {
  if (!event) {
    return "";
  }
  return finalizeConfirmCopy(unshippedEventWaste(event.containers).containerCount);
}

/**
 * Delete-confirmation copy for a specific event, or the empty string while no
 * event is being deleted. Delegates to `deleteConfirmCopy`, mirroring
 * `finalizeConfirmationInfo` above. A linked submission exists if the
 * fragment's `submissions` array has any element at all — deleting the event
 * never retracts it, regardless of that submission's own status.
 */
function deleteConfirmationInfo(event: EpisodicEventItemFragment | null): string {
  if (!event) {
    return "";
  }
  return deleteConfirmCopy(event.containers.length, event.submissions.length > 0);
}

/**
 * Reopens a terminal (FINALIZED/EXPIRED) event back to ACTIVE. Unlike
 * finalize and delete, reopening is non-destructive — nothing is removed,
 * and the only state change is the event's own status/flag — so it fires
 * directly off the card's button rather than behind a confirm dialog.
 */
function useReopenEpisodicEvent() {
  const alerts = useAlerts();
  const [reopenEvent, { loading }] = useMutation(REOPEN_EPISODIC_EVENT_MUTATION, {
    refetchQueries: ["FacilityEpisodicEvents", "FacilityAccumulationSummary"],
  });

  const reopen = async (id: string) => {
    await reopenEvent({
      variables: { id },
      onCompleted: () => {
        alerts.success("Episodic event reopened");
      },
      onError: (error) => {
        alerts.error(error.message ?? "An error occurred while reopening the event");
      },
    });
  };

  return { reopen, isReopening: loading };
}

/** The calendar year of an event's `startDate`. */
function eventYear(event: EpisodicEventItemFragment): number {
  return DateTime.fromISO(event.startDate, { zone: "utc" }).year;
}

/** Distinct event years, newest first — the year picker's options. */
function eventYears(events: EpisodicEventItemFragment[]): number[] {
  return [...new Set(events.map(eventYear))].sort((a, b) => b - a);
}

/**
 * The year picker plus the per-year subpart L allowance note. Only years
 * that actually have events are offered — the picker exists to reach
 * history, not to browse empty years.
 */
function YearPickerRow({
  years,
  year,
  onChange,
}: {
  years: number[];
  year: number;
  onChange: (year: number) => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
      <TextField
        select
        size="small"
        label="Year"
        value={year}
        onChange={(e) => {
          // MUI's select passes the MenuItem value through as-is, so
          // e.target.value is the number itself; the string form is only
          // seen if a browser autofill writes raw text. Match on both.
          const parsed = years.find((candidate) => String(candidate) === String(e.target.value));
          if (parsed !== undefined) {
            onChange(parsed);
          }
        }}
        sx={{ minWidth: 120 }}
      >
        {years.map((candidate) => (
          <MenuItem key={candidate} value={candidate}>
            {candidate}
          </MenuItem>
        ))}
      </TextField>
      <Typography variant="caption" color="text.secondary">
        {YEAR_ALLOWANCE_CAPTION}
      </Typography>
    </Stack>
  );
}

/**
 * One event's card, wired to the ledger's shared handlers. Pulled out of
 * `FacilityEpisodicEvents`'s render so the per-event ACTIVE/terminal branching
 * (Edit/Finalize vs. Reopen) lives in one small component rather than inline
 * in the list's map callback.
 */
function EpisodicEventListItem({
  event,
  isReopening,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  isReopening: boolean;
  onEdit: (event: EpisodicEventItemFragment) => void;
  onFinalize: (event: EpisodicEventItemFragment) => void;
  onReopen: (id: string) => void;
  onDelete: (event: EpisodicEventItemFragment) => void;
}) {
  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const isTerminal =
    event.status === WasteEpisodicEventStatus.Finalized ||
    event.status === WasteEpisodicEventStatus.Expired;
  return (
    <EpisodicEventCard
      event={event}
      onEdit={isActive ? () => onEdit(event) : undefined}
      onFinalize={isActive ? () => onFinalize(event) : undefined}
      onReopen={isTerminal && !isReopening ? () => onReopen(event.id) : undefined}
      onDelete={() => onDelete(event)}
    />
  );
}

function EmptyEventsState({ onDeclare }: { onDeclare: () => void }) {
  return (
    <Box sx={{ py: 4, textAlign: "center", backgroundColor: "grey.50", borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        No episodic events have been declared for this facility yet.
      </Typography>
      <DeclareEventButton onClick={onDeclare} />
    </Box>
  );
}

// A facility declares at most 1 planned + 1 unplanned event per year (40 CFR
// 262 subpart L), so 100 is decades of headroom for the page's default,
// unpaginated fetch — no pagination UI exists yet to page past it.
const EVENTS_PAGE_SIZE = 100;

function FacilityEpisodicEvents({
  tenantId,
  facilityId,
}: {
  tenantId: string;
  facilityId: string;
}) {
  const theme = useTheme();
  const { data, loading } = useQuery(FACILITY_EPISODIC_EVENTS_QUERY, {
    variables: { facilityId, pageSize: EVENTS_PAGE_SIZE },
  });

  const [declareOpen, setDeclareOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EpisodicEventItemFragment | null>(null);
  const [finalizingEvent, setFinalizingEvent] = useState<EpisodicEventItemFragment | null>(null);
  const [deletingEvent, setDeletingEvent] = useState<EpisodicEventItemFragment | null>(null);
  const { reopen, isReopening } = useReopenEpisodicEvent();
  // null = "no explicit choice yet"; the newest event year (usually the
  // current one) is the default view.
  const [pickedYear, setPickedYear] = useState<number | null>(null);

  const facilityName = data?.facility?.name ?? "Facility";

  // Mirrors AccumulationAreasList's trail so the facility is one click away.
  useBreadcrumb([
    { label: "Waste", to: `/o/${tenantId}/waste` },
    { label: "Facilities", to: `/o/${tenantId}/waste/facilities` },
    { label: facilityName, to: `/o/${tenantId}/waste/facilities/${facilityId}` },
    { label: "Episodic events" },
  ]);

  const events = data?.facility?.episodicEvents?.items ?? [];
  const years = eventYears(events);
  const selectedYear = pickedYear ?? years[0] ?? DateTime.now().year;
  const yearEvents = events
    .filter((event) => eventYear(event) === selectedYear)
    .sort((a, b) => b.startDate.localeCompare(a.startDate));

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        // The events are cards; on a white page they had nothing to sit on.
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        flexWrap="wrap"
        gap={2.25}
      >
        <Box>
          <Typography
            component="p"
            variant="overline"
            sx={{ m: 0, mb: 0.5, fontWeight: 700, letterSpacing: ".14em", color: "primary.main" }}
          >
            On-site waste · Episodic events
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
            <Typography
              component="h1"
              variant="h5"
              sx={{ m: 0, fontWeight: 700, lineHeight: 1.1, color: "text.primary" }}
            >
              {facilityName}
            </Typography>
            <FederalGeneratorStatusIndicator
              status={data?.facility?.generationDrivenStatus ?? null}
              active
            />
          </Stack>
          <Typography component="p" variant="body2" sx={{ m: 0, mt: 0.5, color: "text.secondary" }}>
            Temporary generation events that open their own 60-day accumulation window
          </Typography>
        </Box>
        {events.length > 0 && <DeclareEventButton onClick={() => setDeclareOpen(true)} />}
      </Stack>

      {loading && events.length === 0 ? (
        <Skeleton variant="rounded" height={220} />
      ) : events.length === 0 ? (
        <EmptyEventsState onDeclare={() => setDeclareOpen(true)} />
      ) : (
        <>
          <YearPickerRow years={years} year={selectedYear} onChange={setPickedYear} />
          {yearEvents.map((event) => (
            <EpisodicEventListItem
              key={event.id}
              event={event}
              isReopening={isReopening}
              onEdit={setEditingEvent}
              onFinalize={setFinalizingEvent}
              onReopen={reopen}
              onDelete={setDeletingEvent}
            />
          ))}
        </>
      )}

      <EventDialogs
        facilityId={facilityId}
        declareOpen={declareOpen}
        onDeclareClose={() => setDeclareOpen(false)}
        editingEvent={editingEvent}
        onEditClose={() => setEditingEvent(null)}
        finalizingEvent={finalizingEvent}
        onFinalizeCancel={() => setFinalizingEvent(null)}
        deletingEvent={deletingEvent}
        onDeleteCancel={() => setDeletingEvent(null)}
      />
    </Stack>
  );
}

export function EpisodicEventsPage() {
  const { tenantId } = useTenant();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  if (!tenantId) {
    return null;
  }

  if (!facilityId) {
    return <FacilityPickerPrompt />;
  }

  return <FacilityEpisodicEvents tenantId={tenantId} facilityId={facilityId} />;
}
